import { createContext, useContext, useEffect, useState } from 'react';

import { forgetRecords } from '../../recent/core/recentRecords';
import { userStore } from '../../../utils/userStorage';
import { DATE_FORMAT_AUTO, DATE_FORMATS } from '../../../utils/datetime';

/*
 * Client-side UI settings, kept in the browser under the logged-in
 * user (see utils/userStorage) — there is no backend user profile
 * yet. The copy-uuid buttons are a power-user
 * tool and confuse regular users, so they are off by default.
 */
const COPY_UUID_KEY = 'uiSettings.copyUuid';

/* the hosting area, the debitor "invoices" tab and the badged dashboard
   cards are hardwired example data; they are hidden until the user opts
   in (off by default) */
const SHOW_EXAMPLE_DATA_KEY = 'uiSettings.showExampleData';

/* reveals the hosting area backed by real backend data (independent of
   the example-data toggle); off by default while hosting is unfinished */
const SHOW_HOSTING_KEY = 'uiSettings.showHosting';

/* lets the content use the whole window width instead of stopping at a
   readable one; wide tables need it, running text does not, so the
   readable width stays the default (see body.wide-layout in theme.css) */
const WIDE_LAYOUT_KEY = 'uiSettings.wideLayout';

/* the two build stamps in the footer: what a bug report needs and what
   nobody else does, so they are off by default — and while they are,
   the backend is not asked for its version at all */
const SHOW_VERSIONS_KEY = 'uiSettings.showVersions';

/* how a calendar day is written down, in the detail views, the tables
   and the forms alike. "Automatic" follows the UI language — which is
   right until it is not: a browser set to English writes a date input
   as 09/18/1967 in a German session, and the order of the numbers is
   not a matter of taste (see utils/datetime.js) */
const DATE_FORMAT_KEY = 'uiSettings.dateFormat';

/* stored keys (exact or prefix) holding resettable local view state,
   as opposed to the deliberate settings above */
const LOCAL_STATE_KEY_PREFIXES = ['crudSplit:', 'overviewCardOrder'];

const readBoolSetting = (key) => userStore.getItem(key) === 'true';

const readDateFormat = () => {
  const stored = userStore.getItem(DATE_FORMAT_KEY);
  return DATE_FORMATS.includes(stored) ? stored : DATE_FORMAT_AUTO;
};

const defaults = {
  copyUuidEnabled: false,
  setCopyUuidEnabled: () => {},
  showExampleData: false,
  setShowExampleData: () => {},
  showHosting: false,
  setShowHosting: () => {},
  wideLayout: false,
  setWideLayout: () => {},
  showVersions: false,
  setShowVersions: () => {},
  dateFormat: DATE_FORMAT_AUTO,
  setDateFormat: () => {},
  resetLocalSettings: () => {},
};

const UiSettingsContext = createContext(defaults);

export function UiSettingsProvider({ children }) {
  const [copyUuidEnabled, setCopyUuidEnabledState] = useState(() =>
    readBoolSetting(COPY_UUID_KEY)
  );
  const [showExampleData, setShowExampleDataState] = useState(() =>
    readBoolSetting(SHOW_EXAMPLE_DATA_KEY)
  );
  const [showHosting, setShowHostingState] = useState(() =>
    readBoolSetting(SHOW_HOSTING_KEY)
  );
  const [wideLayout, setWideLayoutState] = useState(() =>
    readBoolSetting(WIDE_LAYOUT_KEY)
  );
  const [showVersions, setShowVersionsState] = useState(() =>
    readBoolSetting(SHOW_VERSIONS_KEY)
  );
  const [dateFormat, setDateFormatState] = useState(readDateFormat);

  /* the width caps sit on elements all over the app (tables, the
     centred page column), so the switch is a class on the body rather
     than a prop threaded through every layout */
  useEffect(() => {
    document.body.classList.toggle('wide-layout', wideLayout);
  }, [wideLayout]);

  const persistBool = (key, setState) => (enabled) => {
    setState(enabled);
    userStore.setItem(key, String(enabled));
  };

  const setCopyUuidEnabled = persistBool(
    COPY_UUID_KEY,
    setCopyUuidEnabledState
  );
  const setShowExampleData = persistBool(
    SHOW_EXAMPLE_DATA_KEY,
    setShowExampleDataState
  );
  const setShowHosting = persistBool(SHOW_HOSTING_KEY, setShowHostingState);
  const setWideLayout = persistBool(WIDE_LAYOUT_KEY, setWideLayoutState);
  const setShowVersions = persistBool(SHOW_VERSIONS_KEY, setShowVersionsState);

  const setDateFormat = (format) => {
    if (!DATE_FORMATS.includes(format)) return;
    setDateFormatState(format);
    userStore.setItem(DATE_FORMAT_KEY, format);
  };

  /* wipes the persisted view state (splitter heights, dashboard card
     order) — open pages pick the defaults up on their next mount */
  const resetLocalSettings = () => {
    /* the trail of recently edited records is view state too: the app
       wrote it, nobody asked for it (the bookmarks beside it were set
       by hand and stay). It is cleared through its own store so that
       the card notices without a reload */
    forgetRecords();
    userStore
      .keys()
      .filter((key) =>
        LOCAL_STATE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
      )
      .forEach((key) => userStore.removeItem(key));
  };

  return (
    <UiSettingsContext.Provider
      value={{
        copyUuidEnabled,
        setCopyUuidEnabled,
        showExampleData,
        setShowExampleData,
        showHosting,
        setShowHosting,
        wideLayout,
        setWideLayout,
        showVersions,
        setShowVersions,
        dateFormat,
        setDateFormat,
        resetLocalSettings,
      }}
    >
      {children}
    </UiSettingsContext.Provider>
  );
}

/* outside a provider (isolated component tests) the defaults apply */
export function useUiSettings() {
  return useContext(UiSettingsContext);
}
